import { CommonModule, Location } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { BrowserCacheLocation, BrowserUtils, Configuration, InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

import { AuthConfig, AUTH_CONFIG, normalizeScopesArray, SESSION_POST_LOGIN_REDIRECT_URL } from './auth-config';
import { AuthRoutingModule } from './auth-routing.module';
import { AuthService } from './auth.service';
import { LogoutComponent } from './logout.component';
import { UnauthorizedComponent } from './unauthorized.component';
import { UserStatusComponent } from './user-status/user-status.component';
import { TccUiModule } from '../shared/tcc-ui/tcc-ui.module';

// disable the auth if in an iframe or popup
const AUTH_DISABLED = BrowserUtils.isInIframe() || BrowserUtils.isInPopup();

function msAlInstanceFactory(authConfig: AuthConfig): IPublicClientApplication {
  const config: Configuration = {
    auth: {
      authority: authConfig.authority || 'https://login.microsoftonline.com/f5c71729-7816-4984-867a-0b3a69f99ff8/',
      clientId: authConfig.clientId,
      redirectUri: authConfig.replyUrl || window.location.origin,
      postLogoutRedirectUri: authConfig.postLogoutRedirectUri || `${window.location.origin}/auth/unauthorized`
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    }
  };
  if (authConfig.logLevel) {
    // if config.system is set to undefined it throws an error.  That is why it is added here.
    config.system = {
      loggerOptions: {
        loggerCallback: (_, message) => console.log(message),
        logLevel: authConfig.logLevel || 2,
        piiLoggingEnabled: false
      }
    }
  }
  return new PublicClientApplication(config);
}

function msalInterceptorConfigFactory(authConfig: AuthConfig): MsalInterceptorConfiguration {
  // essentially disable the interceptor by not providing a resource map
  return AUTH_DISABLED
    ? { interactionType: InteractionType.Popup, protectedResourceMap: new Map() }
    : {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map(Array.from(authConfig.protectedResourceMap.entries())
        .map(([res, scopesOrEmpty]) => [res, normalizeScopesArray(scopesOrEmpty)]))
    };
}
function msalGuardConfigFactory(authConfig: AuthConfig): MsalGuardConfiguration {
  const scopes = Array.from(authConfig.protectedResourceMap.values())
    .filter((x): x is NonNullable<typeof x> => !!x) // remove null values.
    .reduce((acc, cur) => acc.concat(cur), [] as string[]) // flatten arrays
    .reduce((acc, cur) => acc.includes(cur) ? acc : acc.concat([cur]), [] as string[]); // make distinct

  return AUTH_DISABLED
      ? { interactionType: InteractionType.Popup }
      : {
        interactionType: InteractionType.Redirect,
        authRequest: (_, route) => {
          sessionStorage.setItem(SESSION_POST_LOGIN_REDIRECT_URL, route.url);
          return {
            scopes
          };
        },
        loginFailedRoute: authConfig.unauthorizedRoute || '/auth/unauthorized'
      };
}

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    TccUiModule,
    AuthRoutingModule
  ],
  declarations: [
    LogoutComponent,
    UnauthorizedComponent,
    UserStatusComponent,
  ],
  exports: [
    UserStatusComponent
  ],
})
export class AuthModule {
  static forRoot(): ModuleWithProviders<AuthModule> {
    return {
      ngModule: AuthModule,
      providers: [
        Location,
        AuthService,
        {
          provide: MSAL_INSTANCE,
          useFactory: msAlInstanceFactory,
          deps: [AUTH_CONFIG]
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: msalInterceptorConfigFactory,
          deps: [AUTH_CONFIG]
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useFactory: msalGuardConfigFactory,
          deps: [AUTH_CONFIG]
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true
        }
      ]
    }
  }
}
